import { IClientsPartners, IFloorplanNavigatorInstances, ISitesHasFloorplanNavigatorInstances } from '@llws/lift-entity-interfaces';
import { IApiListResponse } from '@llws/platform-shared-types';
import dayjs from 'dayjs';
import { CreateComponentPublicInstance } from 'vue';
import { convertQueryCommandToQueryString, Equal } from '@llws/dynamic-router-utils';
import { camelCase, startCase } from 'lodash-es';
import { TFilterConfig, TSelectNextStep, TSelectOptionWithStringValue } from '@/components/RsSidebarFilters.vue';
import clients from '../services/clients';
import partners from '../services/partners';
import properties from '../services/properties';
import sisense from '../services/sisense';
import websiteService from '../services/websites';
import {
  getPRAmenities,
  getPRCitiesWithSisenseIds,
  prCurrentDateOptions,
  prHistoricalDateOptions,
  prSelectOptions,
} from './premium-reporting';
import { checkForRPMFetcher } from './sisense-rpm';
import {
  getInfinityPLCityOptions,
  getInfinityPLPropertyOptions,
  toRentsyncCityId,
} from './infinity-pl-market-insights';
import { adminRequest } from '../request-configs/admin-request';
import { decodeHtml } from './decode';

export const sisenseFilters = {
  dateRange: {
    jaql: {
      title: 'Dates',
      dim: '[dates.date]',
      table: 'dates',
      column: 'date',
      datatype: 'datetime',
      filter: {
        from: '',
        to: '',
      },
      level: 'days',
      merged: true,
    },
  },

  clientId: {
    jaql: {
      title: 'Client ID',
      dim: '[clients.id]',
      datatype: 'numeric',
      filter: {
        explicit: true,
        multiSelection: true,
        members: [] as number[],
      },
    },
  },

  cities: {
    jaql: {
      title: 'Cities',
      dim: '[properties.city_id]',
      datatype: 'numeric',
      filter: {
        explicit: true,
        multiSelection: true,
        members: [] as number[],
      },
    },
  },

  sources: {
    jaql: {
      title: 'Sources',
      dim: '[sources.partner_id]',
      datatype: 'numeric',
      filter: {
        explicit: true,
        multiSelection: true,
        members: [] as number[],
      },
    },
  },

  buildings: {
    jaql: {
      title: 'Properties',
      dim: '[properties.id]',
      datatype: 'numeric',
      filter: {
        explicit: true,
        multiSelection: true,
        members: [] as number[],
      },
    },
  },

  propertyType: {
    jaql: {
      title: 'Property Type',
      dim: '[properties.building_type_id]',
      datatype: 'numeric',
      filter: {
        explicit: true,
        multiSelection: true,
        members: [] as number[],
      },
    },
  },

  province: {
    jaql: {
      title: 'Province',
      dim: '[properties.province_code]',
      datatype: 'text',
      filter: {
        explicit: true,
        multiSelection: true,
        members: [] as string[],
      },
    },
  },

  adCampaigns: {
    jaql: {
      title: 'Ad Campaigns',
      dim: '[Campaigns.Campaign Id]',
      datatype: 'numeric',
      filter: {
        explicit: true,
        multiSelection: true,
        members: [] as number[],
      },
    },
    requestDataConfig: () => ({
      metadata: [
        {
          dim: '[Campaigns.Campaign Id]',
        },
        {
          dim: '[Campaigns.Campaign]',
          sort: 'asc',
        },
        {
          dim: '[Campaigns.Campaign Status]',
          filter: {
            explicit: true,
            multiSelection: true,
            members: ['ENABLED', 'PAUSED'],
          },
        },
      ],
      count: 500,
    }),
  },

  websites: {
    jaql: {
      title: 'Active Websites',
      dim: '[websites.id]',
      datatype: 'numeric',
      filter: {
        explicit: true,
        multiSelection: true,
        members: [] as number[],
      },
    },
  },

  agents: {
    jaql: {
      title: 'Agents',
      dim: '[agents.id]',
      datatype: 'numeric',
      filter: {
        explicit: true,
        multiSelection: true,
        members: [] as number[],
      },
    },
  },

  ownershipGroups: {
    jaql: {
      title: 'Ownership Groups',
      dim: '[ownership_groups.id]',
      datatype: 'numeric',
      filter: {
        explicit: true,
        multiSelection: true,
        members: [] as number[],
      },
    },
  },

  marketInsightsFSAs: {
    jaql: {
      title: 'Neighbourhood FSAs',
      dim: '[consolidated_listings_snapshot.fsa]',
      datatype: 'text',
      filter: {
        explicit: true,
        multiSelection: true,
        members: [],
      },
    },
  },

  marketInsightsProvince: {
    jaql: {
      title: 'Province',
      dim: '[geo_cities_consolidated.province]',
      column: 'province',
      datatype: 'text',
      filter: {
        explicit: true,
        multiSelection: false,
        members: [] as string[],
      },
    },
  },

  marketInsightsCity: {
    noFilterOnEmptyMembers: true,
    jaql: {
      title: 'City',
      dim: '[geo_cities_consolidated.id]',
      column: 'id',
      datatype: 'numeric',
      filter: {
        explicit: true,
        multiSelection: false,
        members: [] as number[],
      },
    },
  },

  marketInsightsCityRadio: {
    splitValue: true,
    jaql: {
      title: 'City',
      dim: '[geo_cities_consolidated.id]',
      column: 'id',
      datatype: 'numeric',
      filter: {
        explicit: true,
        multiSelection: false,
        members: [],
      },
    },
  },

  marketInsightsCityMulti: {
    jaql: {
      title: 'City',
      dim: '[geo_cities_consolidated.id]',
      column: 'id',
      datatype: 'numeric',
      filter: {
        explicit: true,
        multiSelection: true,
        members: [] as number[],
      },
    },
  },

  marketInsightsBedrooms: {
    jaql: {
      title: 'Bedrooms',
      dim: '[beds_consolidated.bed]',
      datatype: 'text',
      filter: {
        explicit: true,
        multiSelection: true,
        members: [],
      },
    },
  },

  marketInsightsEightMonths: {
    jaql: {
      title: 'Timeline Range',
      dim: '[consolidated_listings_snapshot.snapshot_date]',
      table: 'consolidated_listings_snapshot',
      column: 'snapshot_date',
      datatype: 'datetime',
      filter: {
        from: '',
        to: '',
        multiSelection: true,
      },
      level: 'days',
      merged: true,
    },
  },

  marketInsightsIncrementMonths: {
    jaql: {
      title: 'Timeline Range',
      dim: '[consolidated_listings_snapshot.snapshot_date (Calendar)]',
      table: 'consolidated_listings_snapshot',
      column: 'snapshot_date',
      datatype: 'datetime',
      filter: {
        from: '',
        to: '',
        multiSelection: true,
      },
      level: 'days',
      merged: true,
    },
  },

  fpnSites: {
    jaql: {
      title: 'Active FPNs',
      dim: '[sites_has_floorplan_navigator_instances.id]',
      column: 'id',
      datatype: 'numeric',
      filter: {
        explicit: true,
        multiSelection: true,
        members: [],
      },
    },
  },

  adCampaignDates: {
    jaql: {
      title: 'Dates',
      dim: '[dates.date]',
      table: 'dates',
      column: 'date',
      datatype: 'datetime',
      filter: {
        from: '',
        to: '',
      },
      level: 'days',
      merged: true,
    },
  },

  marketInsightsBuildingType: {
    jaql: {
      title: 'Property Type',
      dim: '[consolidated_listings_snapshot.property_type]',
      column: 'property_type',
      datatype: 'text',
      filter: {
        explicit: true,
        multiSelection: true,
        members: [] as string[],
      },
    },
  },

  marketInsightsAmenities: {
    multiJaql: (members: string[], options: TSelectOptionWithStringValue[]) => options.map((option) => ({
      jaql: {
        title: option.text,
        dim: `[consolidated_listings_snapshot.${option.value}]`,
        column: option.value,
        datatype: 'text',
        filter: {
          explicit: true,
          multiSelection: true,
          members: members.includes(option.value) ? ['1'] : [],
        },
      },
    })),
  },

  marketInsightsUtilities: {
    multiJaql: (members: string[], options: TSelectOptionWithStringValue[]) => options.map((option) => ({
      jaql: {
        title: option.text,
        dim: `[consolidated_listings_snapshot.${option.value}]`,
        column: option.value,
        datatype: 'text',
        filter: {
          explicit: true,
          multiSelection: true,
          members: members.includes(option.value) ? ['1'] : [],
        },
      },
    })),
  },

  marketInsightsAgeOfConstruction: {
    jaql: {
      title: 'Age of Construction',
      dim: '[consolidated_listings_snapshot.age_of_construction]',
      column: 'age_of_construction',
      datatype: 'text',
      filter: {
        explicit: true,
        // Deliberately multi-selection even though the control is a single select. Sisense discards a
        // single-selection member filter that has no members, so clearing the filter would leave the previously
        // applied selection on the dashboard; empty members on a multi-selection filter reads as "select all".
        multiSelection: true,
        members: [] as string[],
      },
    },
  },

  marketInsightsNumberOfStories: {
    jaql: {
      title: 'Number of Stories',
      dim: '[consolidated_listings_snapshot.number_of_stories]',
      column: 'number_of_stories',
      datatype: 'text',
      filter: {
        explicit: true,
        multiSelection: true,
        members: [] as string[],
      },
    },
  },

  marketInsightsParkingAvailable: {
    jaql: {
      title: 'Parking Available',
      dim: '[consolidated_parking.parking_available]',
      column: 'parking_available',
      datatype: 'text',
      filter: {
        explicit: true,
        // Multi-selection for the same reason as `marketInsightsAgeOfConstruction` above.
        multiSelection: true,
        members: [] as string[],
      },
    },
  },

  marketInsightsParkingIncluded: {
    jaql: {
      title: 'Parking Included',
      dim: '[consolidated_parking.parking_included]',
      column: 'parking_included',
      datatype: 'text',
      filter: {
        explicit: true,
        // Multi-selection for the same reason as `marketInsightsAgeOfConstruction` above.
        multiSelection: true,
        members: [] as string[],
      },
    },
  },

  marketInsightsParkingType: {
    jaql: {
      title: 'Parking Type',
      dim: '[consolidated_parking.parking_type]',
      column: 'parking_type',
      datatype: 'text',
      filter: {
        explicit: true,
        multiSelection: true,
        members: [] as string[],
      },
    },
  },

  marketInsightsPets: {
    jaql: {
      title: 'Pet Friendly',
      dim: '[consolidated_listings_snapshot.pet_friendly]',
      column: 'pet_friendly',
      datatype: 'numeric',
      filter: {
        explicit: true,
        multiSelection: true,
        members: [] as number[],
      },
    },
  },

  marketInsightsPortfolioBuildingType: {
    jaql: {
      title: 'Property Type',
      dim: '[units.property_type]',
      column: 'property_type',
      datatype: 'text',
      filter: {
        explicit: true,
        multiSelection: true,
        members: [] as number[],
      },
    },
  },

  marketInsightsPortfolioFSA: {
    jaql: {
      title: 'FSAs',
      dim: '[units.FSA]',
      column: 'FSA',
      datatype: 'text',
      filter: {
        explicit: true,
        multiSelection: true,
        members: [] as number[],
      },
    },
  },

  marketInsightsPortfolioBuildings: {
    jaql: {
      title: 'Properties',
      dim: '[units.building_id]',
      column: 'building_id',
      datatype: 'numeric',
      filter: {
        explicit: true,
        multiSelection: true,
        members: [] as number[],
      },
    },
  },

  infinityPLCities: {
    jaql: {
      title: 'City',
      dim: '[geo_cities_consolidated.id]',
      column: 'id',
      datatype: 'numeric',
      filter: {
        explicit: true,
        multiSelection: false,
        members: [],
      },
    },
  },
};

/**
 * If the individual filters here need mapping to filter config controls, remove them
 * from this declaration.
 */
const {
  dateRange,
  clientId,
  province,
  infinityPLCities,
  ...sisenseFiltersToFilterConfigMap
} = sisenseFilters;

/**
 * Maps filter string values in the sisense dashboards to JAQL query objects.
 * This step ensures that we know what filter format goes with which set of filters
 * available to a dashboard.
 *
 * Furthermore, we can reuse filters in the `sisenseFilters` object to stay a little DRYer.
 */
export const filterKeysToJaqlMap = {
  ...sisenseFiltersToFilterConfigMap,
  dates: sisenseFilters.dateRange,
  marketInsightsFSAWithCondition: sisenseFilters.marketInsightsFSAs,
  marketInsightsPortfolioCompareCity: sisenseFilters.marketInsightsCityRadio,
  marketInsightsPortfolioCompareBeds: sisenseFilters.marketInsightsBedrooms,
  marketInsightsCompareBuildingType: sisenseFilters.marketInsightsBuildingType,
  marketInsightsCompareAgeOfConstruction: sisenseFilters.marketInsightsAgeOfConstruction,
  marketInsightsCompareNumberOfStories: sisenseFilters.marketInsightsNumberOfStories,
  marketInsightsCompareFSA: sisenseFilters.marketInsightsFSAs,
  infinityPLPortfolioCompareCity: sisenseFilters.infinityPLCities,
  infinityPLPortfolioBuildings: sisenseFilters.marketInsightsPortfolioBuildings,
  infinityPLPortfolioFSA: sisenseFilters.marketInsightsPortfolioFSA,
  infinityPLCompareFSA: sisenseFilters.marketInsightsFSAs,
  marketInsightsHistoricalAgeOfConstruction: sisenseFilters.marketInsightsAgeOfConstruction,
  marketInsightsHistoricalNumberOfStories: sisenseFilters.marketInsightsNumberOfStories,
};

type TOmitSisenseFilterKeys = 'dateRange' | 'clientId';

/**
 * Get all possible filters mapped to filter sidebar component configuration settings.
 * For each of the `filterKeysToJaqlMap` keys, return the associated filter control object that the filter sidebar's config can use.
 * Filter keys are defined in the `sisense-dashboards-v2.ts files under each dashboard object's `filters` array.
 * In order to define a new filter key, it must be added to `filterKeysToJaqlMap` object in `sisense-filters-v2.ts`.
 * Once done, this function will throw type errors until the added filter key has an associated UI control.
 * @param vm the component instance (pass `this` within components)
 * @returns mapped configuration to filter keys.
 */
export const getFilterComponentConfig = async (
  vm: CreateComponentPublicInstance,
): Promise<Record<keyof Omit<typeof filterKeysToJaqlMap, TOmitSisenseFilterKeys>, TFilterConfig>> => {
  const currentClientId = vm.$rsGlobals.$currentClient?.id;
  const isMarketInsightsRoute = vm.$route?.path.includes('/market-insights');
  const isMarketInsightsTeaserRoute = vm.$route?.path.includes('/market-data-teaser');
  const isInfinityPortfolioComparisonRoute = vm.$route?.path.includes('/market-insights/infinity-portfolio-comparison');
  const suppressNoCitySubscriptionError = isMarketInsightsTeaserRoute || isInfinityPortfolioComparisonRoute;

  type TPRCitiesWithSisenseIdsResponse = Awaited<ReturnType<typeof getPRCitiesWithSisenseIds>>;

  let prCitiesRes: TPRCitiesWithSisenseIdsResponse | null = null;
  let infinityPLCityOptions: TSelectOptionWithStringValue[] | null = null;

  const getInfinityPLCityOptionsCached = async () => {
    if (infinityPLCityOptions) {
      return infinityPLCityOptions;
    }

    if (!currentClientId) {
      return [];
    }

    try {
      const options = await getInfinityPLCityOptions(currentClientId);
      infinityPLCityOptions = options;
      return options;
    } catch (err) {
      console.error(err);
      return [];
    }
  };

  /**
   * Resolves Sisense analytics city IDs from the dependent filter value
   * back to Rentsync city IDs, then returns only the properties in those cities.
   */
  const getInfinityPLPropertyOptionsByCity = async (dependentValue: string[]) => {
    if (!currentClientId || !dependentValue.length) {
      return [];
    }

    try {
      const selectedAnalyticsCityIds = dependentValue
        .flatMap((value) => `${value}`.split(','))
        .map((value) => value.trim())
        .filter(Boolean);

      if (!selectedAnalyticsCityIds.length) {
        return [];
      }

      const cityOptions = await getInfinityPLCityOptionsCached();
      const selectedRentsyncCityIds = new Set(
        cityOptions
          .filter((city) => selectedAnalyticsCityIds.includes(`${city.value}`))
          .map((city) => toRentsyncCityId(city))
          .filter((cityId) => cityId > 0),
      );

      if (!selectedRentsyncCityIds.size) {
        return [];
      }

      const options = await getInfinityPLPropertyOptions(currentClientId, selectedRentsyncCityIds);

      // Filter property options by user-level permissions
      return options.filter(
        (option) => {
          const id = Number(option.value);
          return !isNaN(id) && vm.$rsGlobals.$userHasAccess('buildings', 'read', id);
        },
      );
    } catch (err) {
      console.error(err);
      return [];
    }
  };

  if (isMarketInsightsRoute && !isInfinityPortfolioComparisonRoute) {
    try {
      prCitiesRes = await getPRCitiesWithSisenseIds(vm, true, suppressNoCitySubscriptionError);
    } catch (err) {
      console.error(err);
      prCitiesRes = null;
    }
  }

  if (isInfinityPortfolioComparisonRoute) {
    infinityPLCityOptions = await getInfinityPLCityOptionsCached();
  }

  const prCities = prCitiesRes;
  const prSubscribedCities = prCitiesRes
    ? { cities: prCitiesRes.subscribedCities }
    : null;

  const getPRCitiesWithSisenseIdsCached = async () => {
    if (prCitiesRes) {
      return prCitiesRes;
    }

    try {
      return await getPRCitiesWithSisenseIds(vm, undefined, suppressNoCitySubscriptionError);
    } catch (err) {
      console.error(err);
      return {
        subscribedCities: [],
        cities: [],
        defaultFSAs: [],
      };
    }
  };

  const marketInsightsFSABase: TFilterConfig = {
    label: vm.$ts('marketInsights.filters.fsaBasedOnCity'),
    type: 'checkboxes',
    fetchInitialValueFromQueryString: true,
    dependsOn: {
      base: {
        must: {
          haveValue: true,
        },
        soThatFilterCan: {
          fetchOptions: async (dependentValue) => {
            try {
              const response = await sisense.getPremiumReportingFSAs(
                dependentValue.flatMap((v) => v.split(',')).map((v) => Number(v)),
                true,
              );
              return response?.map((fsa) => ({ text: `${fsa.fsa}`, value: `${fsa.fsa}` })) ?? [];
            } catch (err) {
              console.error(err);
              return [];
            }
          },
        },
      },
    },
  };

  const marketInsightsCityRadioBase: TFilterConfig = {
    label: vm.$ts('marketInsights.filters.labels.city'),
    type: 'radio',
    helpText: vm.$ts('marketInsights.filters.helpText.singleCityScope'),
    fetchInitialValueFromQueryString: true,
    clearButtonBehaviour: 'reset',
    required: true,
    fetcher: async () => {
      const res = await getPRCitiesWithSisenseIdsCached();
      return res.cities ?? [];
    },
    ...prCities?.cities?.length && {
      value: `${prCities.cities[0]?.value}`,
    },
  };

  const allMarketInsightsProvinceOptions: TSelectOptionWithStringValue[] = [
    { text: 'BC', value: 'BC' },
    { text: 'AB', value: 'AB' },
    { text: 'SK', value: 'SK' },
    { text: 'MB', value: 'MB' },
    { text: 'ON', value: 'ON' },
    { text: 'QC', value: 'QC' },
    { text: 'NB', value: 'NB' },
    { text: 'NS', value: 'NS' },
    { text: 'NL', value: 'NL' },
    { text: 'PE', value: 'PE' },
  ];

  const subscribedProvinceCodes = new Set(
    (prSubscribedCities?.cities ?? [])
      .map((city) => `${city.city?.province?.provinceCode ?? ''}`.toUpperCase())
      .filter((code) => !!code),
  );

  const marketInsightsCityIdsByProvince = (prSubscribedCities?.cities ?? []).reduce<Record<string, Set<number>>>(
    (acc, city) => {
      const provinceCode = `${city.city?.province?.provinceCode ?? ''}`.toUpperCase();
      const cityId = Number(city.cityId ?? 0);

      if (!provinceCode || !cityId) {
        return acc;
      }

      if (!acc[provinceCode]) {
        acc[provinceCode] = new Set<number>();
      }

      acc[provinceCode].add(cityId);
      return acc;
    },
    {},
  );

  const defaultMarketInsightsProvince = `${vm.$rsGlobals.$currentClient.city?.province?.provinceCode ?? ''}`.toUpperCase();
  const isMarketDataTeaserRoute = vm.$route?.path.includes('/market-data-teaser');
  const isCurrentMarketDataRoute = vm.$route?.path.includes('/market-data')
    || vm.$route?.path.includes('/market-comparison-neighbourhood');
  const isMarketComparisonNeighbourhoodRoute = vm.$route?.path.includes('/market-comparison-neighbourhood');

  const marketInsightsProvinceOptions = isMarketDataTeaserRoute
    ? allMarketInsightsProvinceOptions
    : allMarketInsightsProvinceOptions.filter((opt) => subscribedProvinceCodes.has(opt.value));

  const initialMarketInsightsProvince = marketInsightsProvinceOptions
    .some((opt) => opt.value === defaultMarketInsightsProvince)
    ? defaultMarketInsightsProvince
    : `${marketInsightsProvinceOptions[0]?.value ?? ''}`;

  const getMarketInsightsCitiesByProvince = (provinceCode: string) => {
    const cityIds = marketInsightsCityIdsByProvince[provinceCode.toUpperCase()];

    if (!cityIds?.size) {
      return [];
    }

    return (prCities?.cities ?? []).filter((city) => {
      const rentsyncCityId = Number(('subText' in city ? city.subText : city.value) ?? city.value);
      return cityIds.has(rentsyncCityId);
    });
  };

  const getPremiumReportingFSAOptions = async (cityValues: Array<string | number>) => {
    try {
      const values = cityValues.map((v) => Number(v)).filter((v) => v > 0);

      if (!values.length) {
        return [];
      }

      const response = await sisense.getPremiumReportingFSAs(values, true);
      return response?.map((fsa) => ({ text: `${fsa.fsa}`, value: `${fsa.fsa}` })) ?? [];
    } catch (err) {
      console.error(err);
      return [];
    }
  };

  const getDefaultFSAValuesForCity = async (cityValue: string, max: number = 4) => {
    const options = await getPremiumReportingFSAOptions([cityValue]);
    return options.slice(0, max).map((fsa) => `${fsa.value}`);
  };

  const marketInsightsProvinceCityStep: TSelectNextStep = {
    label: vm.$ts('marketInsights.filters.labels.city'),
    key: 'marketInsightsCity',
    behaviour: 'disabled',
    maxSelect: 1,
    fetcher: async ({ previousValue }) => {
      const provinceCode = Array.isArray(previousValue)
        ? `${previousValue[0] ?? ''}`
        : `${previousValue ?? ''}`;

      if (!provinceCode) {
        return [];
      }

      return getMarketInsightsCitiesByProvince(provinceCode);
    },
  };

  const marketInsightsProvinceFSAStep: TSelectNextStep = {
    label: vm.$ts('marketInsights.filters.labels.fsa'),
    key: 'marketInsightsFSAs',
    behaviour: 'disabled',
    maxSelect: 4,
    fetcher: async ({ previousValue }) => {
      const values = typeof previousValue === 'string'
        ? previousValue.split(',')
        : previousValue;

      return getPremiumReportingFSAOptions(values);
    },
  };

  const initialMarketInsightsProvinceCities = initialMarketInsightsProvince
    ? getMarketInsightsCitiesByProvince(initialMarketInsightsProvince)
    : [];

  const marketInsightsDefaultCityForProvince = (
    isCurrentMarketDataRoute
    && initialMarketInsightsProvinceCities[0]?.value
  )
    ? `${initialMarketInsightsProvinceCities[0]?.value}`
    : '';

  let marketInsightsDefaultFSAValues: string[] = [];

  if (
    isMarketComparisonNeighbourhoodRoute
    && marketInsightsDefaultCityForProvince
    && !vm.$route?.query?.marketInsightsFSAWithCondition
  ) {
    marketInsightsDefaultFSAValues = await getDefaultFSAValuesForCity(marketInsightsDefaultCityForProvince);
  }

  const marketInsightsProvinceBase: TFilterConfig = {
    label: vm.$ts('marketInsights.filters.labels.province'),
    type: 'select',
    fetchInitialValueFromQueryString: true,
    clearButtonBehaviour: 'reset',
    fetcher: async () => marketInsightsProvinceOptions,
    ...(isCurrentMarketDataRoute && initialMarketInsightsProvince) && {
      value: initialMarketInsightsProvince,
    },
    nestedSelect: isCurrentMarketDataRoute
      ? [
        {
          ...marketInsightsProvinceCityStep,
          ...(marketInsightsDefaultCityForProvince && {
            value: [marketInsightsDefaultCityForProvince],
          }),
        },
      ]
      : [marketInsightsProvinceCityStep, marketInsightsProvinceFSAStep],
  };

  const marketInsightsSurveyedMarkets = ['Toronto', 'Ottawa', 'Calgary', 'Edmonton', 'Montreal', 'Vancouver'];

  const marketInsightsSurveyedMarketCityIds = (prCities?.cities ?? [])
    .filter((city) => marketInsightsSurveyedMarkets
      .some((market) => `${city.text}`.toLowerCase().includes(market.toLowerCase())))
    .map((city) => `${city.value}`)
    .filter((value) => value);

  const withSurveyedMarketOptions = (options: TSelectOptionWithStringValue[]) => async (dependentValue: string[]) => {
    if (!dependentValue.length || !marketInsightsSurveyedMarketCityIds.length) {
      return [];
    }

    const hasSurveyedMarket = dependentValue
      .some((value) => marketInsightsSurveyedMarketCityIds.includes(`${value}`));

    return hasSurveyedMarket ? options : [];
  };

  // Single-select filters fall back to the empty value on page load and when cleared, so that value needs a
  // selectable option - unlike checkboxes, a select has no other way back to an unset state once a user picks one.
  const marketInsightsAgeOfConstructionOptions: TSelectOptionWithStringValue[] = [
    { text: vm.$ts('filters.all'), value: '' },
    { text: vm.$ts('marketInsights.filters.ageOfConstruction.post2010'), value: 'Post 2010' },
    { text: vm.$ts('marketInsights.filters.ageOfConstruction.pre2010'), value: 'Pre 2010' },
  ];

  const marketInsightsNumberOfStoriesOptions: TSelectOptionWithStringValue[] = [
    { text: vm.$ts('marketInsights.filters.numberOfStories.highRise'), value: 'High Rise' },
    { text: vm.$ts('marketInsights.filters.numberOfStories.midRise'), value: 'Mid Rise' },
    { text: vm.$ts('marketInsights.filters.numberOfStories.lowRise'), value: 'Low Rise' },
  ];

  // Needs an unset option for the same reason as `marketInsightsAgeOfConstructionOptions` above.
  const marketInsightsParkingAvailableOptions: TSelectOptionWithStringValue[] = [
    { text: vm.$ts('filters.all'), value: '' },
    { text: vm.$ts('marketInsights.filters.parkingAvailable.available'), value: 'Parking Available' },
    { text: vm.$ts('marketInsights.filters.parkingAvailable.unavailable'), value: 'Parking Unavailable' },
  ];

  const marketInsightsParkingIncludedOptions: TSelectOptionWithStringValue[] = [
    { text: vm.$ts('filters.all'), value: '' },
    { text: vm.$ts('marketInsights.filters.parkingIncluded.included'), value: 'Parking Included' },
    { text: vm.$ts('marketInsights.filters.parkingIncluded.notIncluded'), value: 'Parking Not Included' },
  ];

  const marketInsightsParkingTypeOptions: TSelectOptionWithStringValue[] = [
    { text: vm.$ts('marketInsights.filters.parkingType.driveway'), value: 'Driveway' },
    { text: vm.$ts('marketInsights.filters.parkingType.electric'), value: 'Electric' },
    { text: vm.$ts('marketInsights.filters.parkingType.garage'), value: 'Garage' },
    { text: vm.$ts('marketInsights.filters.parkingType.indoor'), value: 'Indoor' },
    { text: vm.$ts('marketInsights.filters.parkingType.outdoor'), value: 'Outdoor' },
    { text: vm.$ts('marketInsights.filters.parkingType.underground'), value: 'Underground' },
    { text: vm.$ts('marketInsights.filters.parkingType.valet'), value: 'Valet' },
  ];

  const marketInsightsBuildingTypeBase: TFilterConfig = {
    label: vm.$ts('marketInsights.filters.labels.propertyType'),
    type: 'checkboxes',
    fetchInitialValueFromQueryString: true,
    fetcher: async () => prSelectOptions.propertyTypes,
  };

  const marketInsightsBedroomsBase: TFilterConfig = {
    label: vm.$ts('marketInsights.filters.labels.bedrooms'),
    type: 'checkbox_buttons',
    fetchInitialValueFromQueryString: true,
    fetcher: async () => prSelectOptions.bedrooms,
  };

  const buildingsBase: TFilterConfig = {
    label: vm.$ts('reporting.filters.buildings.label'),
    type: 'checkboxes',
    fetchInitialValueFromQueryString: true,
    fetcher: checkForRPMFetcher(vm, 'rpmProperties', async () => {
      try {
        const response = await properties.getPropertyOptions();
        return (response.data ?? []).map((b) => ({
          text: b.buildingName,
          value: `${b.id}`,
        }));
      } catch (err) {
        console.error(err);
        return [];
      }
    }),
  };

  const buildingTypesBase: TFilterConfig = {
    label: vm.$ts('reporting.filters.propertyType.label'),
    type: 'checkboxes',
    fetchInitialValueFromQueryString: true,
    fetcher: checkForRPMFetcher(vm, 'rpmPropertyTypes', async () => {
      try {
        const response = await properties.getClientBuildingTypes();
        return (response ?? []).map((bt) => ({
          text: bt.name,
          value: `${bt.id}`,
        }));
      } catch (err) {
        console.error(err);
        return [];
      }
    }),
  };

  return {
    dates: {
      label: vm.$ts('reporting.filters.dates.label'),
      type: 'date_range',
      fetchInitialValueFromQueryString: true,
      clearButtonBehaviour: 'reset',
      value: `${dayjs().subtract(30, 'days').format('YYYY-MM-DD')},${dayjs().subtract(1, 'days').format('YYYY-MM-DD')}`,
      controlConfig: {
        noFuture: true,
        yearLimit: 3,
        excludeTodayForPresets: true,
      },
    },

    adCampaignDates: {
      label: vm.$ts('reporting.filters.dates.label'),
      type: 'date_range',
      fetchInitialValueFromQueryString: true,
      clearButtonBehaviour: 'reset',
      value: `${dayjs().subtract(30, 'days').format('YYYY-MM-DD')},${dayjs().subtract(1, 'days').format('YYYY-MM-DD')}`,
      controlConfig: {
        noFuture: true,
        yearLimit: 1,
        excludeTodayForPresets: true,
      },
    },

    sources: {
      label: vm.$ts('reporting.filters.sources.label'),
      type: 'checkboxes',
      fetchInitialValueFromQueryString: true,
      fetcher: checkForRPMFetcher(vm, 'rpmSources', async () => {
        try {
          const response = await partners.getClientPartners() as IApiListResponse<IClientsPartners, null>;
          return (response.data ?? [])
            .map((cp) => ({ text: `${cp.partner?.name}`, value: `${cp.partnerId}` }))
            .sort((a, b) => (a.text > b.text ? 1 : -1));
        } catch (err) {
          console.error(err);
          return [];
        }
      }),
    },

    buildings: buildingsBase,

    propertyType: buildingTypesBase,

    cities: {
      label: vm.$ts('reporting.filters.cities.label'),
      type: 'checkboxes',
      helpText: vm.$ts('reporting.filters.cities.helpText'),
      fetchInitialValueFromQueryString: true,
      fetcher: checkForRPMFetcher(vm, 'rpmCities', async () => {
        try {
          const response = await clients.getClientCities(vm?.$rsGlobals.$currentClient.id ?? 0);
          return (response.data ?? []).map((c) => ({
            text: `${c.cityName}, ${c.province?.provinceCode}`,
            value: `${c.id}`,
          }));
        } catch (err) {
          console.error(err);
          return [];
        }
      }),
    },

    websites: {
      label: vm.$ts('reporting.filters.websites.label'),
      type: 'checkboxes',
      fetchInitialValueFromQueryString: true,
      fetcher: checkForRPMFetcher(vm, 'rpmWebsites', async () => {
        try {
          const response = await websiteService.get({ limit: 9999, statusFilterValue: 'active' });
          return (response.data ?? []).map((s) => ({ text: s.name, value: `${s.id}` }));
        } catch (err) {
          console.error(err);
          return [];
        }
      }),
    },

    ownershipGroups: {
      label: vm.$ts('reporting.filters.ownershipGroups.label'),
      type: 'checkboxes',
      fetchInitialValueFromQueryString: true,
      fetcher: checkForRPMFetcher(vm, 'rpmOwnershipGroups', async () => {
        try {
          const response = await clients.getClientOwnershipGroups(vm?.$rsGlobals.$currentClient.id ?? 0);
          return [
            ...(response.data ?? []).map((og) => ({
              text: og.name,
              value: `${og.id}`,
            })),
          ];
        } catch (err) {
          console.error(err);
          return [];
        }
      }),
    },

    agents: {
      label: vm.$ts('reporting.filters.agents.label'),
      type: 'checkboxes',
      fetchInitialValueFromQueryString: true,
      fetcher: async () => ([]),
    },

    adCampaigns: {
      label: vm.$ts('reporting.filters.adCampaigns.label'),
      type: 'checkboxes',
      fetchInitialValueFromQueryString: true,
      fetcher: async () => {
        try {
          const response = await sisense.getGoogleAdCampaigns();
          return (response ?? []).map((ad) => ({ text: ad.name, value: `${ad.id}` }));
        } catch (err) {
          console.error(err);
          return [];
        }
      },
    },

    marketInsightsCityRadio: marketInsightsCityRadioBase,

    marketInsightsProvince: marketInsightsProvinceBase,

    marketInsightsCity: {
      label: vm.$ts('marketInsights.filters.labels.city'),
      type: 'select',
      fetchInitialValueFromQueryString: true,
      display: {
        hidden: true,
      },
    },

    marketInsightsCityMulti: {
      label: vm.$ts('marketInsights.filters.labels.city'),
      type: 'checkboxes',
      helpText: vm.$ts('marketInsights.filters.helpText.upToFourCities'),
      fetchInitialValueFromQueryString: true,
      clearButtonBehaviour: 'reset',
      required: true,
      controlConfig: {
        max: 4,
      },
      fetcher: async () => {
        const res = await getPRCitiesWithSisenseIdsCached();
        return res.cities ?? [];
      },
      ...prCities?.cities?.length && {
        value: `${prCities.cities.map((prc) => prc.value).join(',')}`,
      },
    },

    marketInsightsFSAs: {
      label: vm.$ts('marketInsights.filters.labels.neighbourhoodFsas'),
      type: 'select',
      fetchInitialValueFromQueryString: true,
      clearButtonBehaviour: 'reset',
      required: true,
      fetcher: async () => {
        const res = await getPRCitiesWithSisenseIdsCached();
        return res.cities ?? [];
      },
      ...prCities?.cities?.length && prCities.cities[0]?.value && {
        value: `${prCities.cities[0].value}`,
      },
      nestedSelect: {
        label: vm.$ts('marketInsights.filters.labels.fsa'),
        key: 'fsa',
        behaviour: 'disabled',
        maxSelect: 4,
        value: prCities?.defaultFSAs,
        fetcher: async ({ previousValue }) => {
          const values = typeof previousValue === 'string'
            ? previousValue.split(',')
            : previousValue;

          return getPremiumReportingFSAOptions(values);
        },
      },
    },

    marketInsightsBedrooms: {
      label: vm.$ts('marketInsights.filters.labels.bedrooms'),
      type: 'checkbox_buttons',
      fetchInitialValueFromQueryString: true,
      fetcher: async () => prSelectOptions.bedrooms,
    },

    marketInsightsEightMonths: {
      label: vm.$ts('marketInsights.filters.labels.timelineRange'),
      type: 'date_range',
      fetchInitialValueFromQueryString: true,
      clearButtonBehaviour: 'reset',
      required: true,
      value: `${dayjs().startOf('month').format('YYYY-MM-DD')},${dayjs().endOf('month').format('YYYY-MM-DD')}`,
      controlConfig: {
        hideEmptyOption: true,
        hideCustomRangeControl: true,
        options: prCurrentDateOptions,
      },
    },

    marketInsightsIncrementMonths: {
      label: vm.$ts('marketInsights.filters.labels.timelineRange'),
      type: 'date_range',
      fetchInitialValueFromQueryString: true,
      clearButtonBehaviour: 'reset',
      required: true,
      value: `${dayjs().subtract(12, 'month').startOf('month').format('YYYY-MM-DD')},${dayjs().endOf('month').format('YYYY-MM-DD')}`,
      controlConfig: {
        hideEmptyOption: true,
        hideCustomRangeControl: true,
        options: prHistoricalDateOptions,
      },
    },

    fpnSites: {
      label: vm.$ts('reporting.filters.fpnSites.label'),
      type: 'checkboxes',
      fetchInitialValueFromQueryString: true,
      fetcher: async () => {
        try {
          const url = '/web-components-api/floorplan_navigator_instances';

          const querystring = convertQueryCommandToQueryString({
            alias: 'fni',
            relations: [
              { path: 'fni.sitesHasFloorplanNavigatorInstances', alias: 'shfni' },
              { path: 'shfni.site', alias: 's' },
              { path: 'fni.building', alias: 'b' },
            ],
            select: [
              'shfni.id',
              'shfni.domain',
              'shfni.siteId',
              'shfni.status',
              'fni.id',
              'fni.buildingId',
              'b.id',
              'b.clientId',
              'b.buildingName',
              's.id',
              's.name',
            ],
            where: [
              Equal('shfni.status', 'enabled'),
              Equal('b.clientId', vm?.$rsGlobals.$currentClient.id),
            ],
            orderBy: [
              { field: 'b.buildingName', direction: 'asc' },
            ],
            limit: 'false',
          });

          const res = await adminRequest.get<IFloorplanNavigatorInstances[]>(`${url}${querystring}`);

          return res.data?.flatMap((ins) => {
            const building = decodeHtml(ins?.building?.buildingName);

            return (ins.sitesHasFloorplanNavigatorInstances ?? []).map((sins) => {
              const site = sins?.site?.siteUrl ?? sins.site?.name ?? sins.domain;
              return {
                text: `${building} (${site})`,
                value: `${sins.id}`,
              };
            });
          }) ?? [];
        } catch (err) {
          console.error(err);
          return [];
        }
      },
    },

    marketInsightsBuildingType: {
      label: vm.$ts('marketInsights.filters.labels.propertyType'),
      type: 'checkboxes',
      fetchInitialValueFromQueryString: true,
      fetcher: async () => prSelectOptions.propertyTypes,
    },

    marketInsightsAmenities: {
      label: vm.$ts('marketInsights.filters.labels.amenities'),
      type: 'checkboxes',
      fetchInitialValueFromQueryString: true,
      fetcher: async () => {
        try {
          const amenities = await getPRAmenities();

          return amenities.map((amenity) => ({
            text: startCase(camelCase(amenity.standardAmenity)),
            value: amenity.key,
          }));
        } catch (err) {
          console.error(err);
          return [];
        }
      },
    },

    marketInsightsUtilities: {
      label: vm.$ts('marketInsights.filters.labels.utilities'),
      type: 'checkboxes',
      fetchInitialValueFromQueryString: true,
      fetcher: async () => prSelectOptions.utilities,
    },

    marketInsightsAgeOfConstruction: {
      label: vm.$ts('marketInsights.filters.labels.ageOfConstruction'),
      type: 'select',
      helpText: vm.$ts('marketInsights.filters.helpText.ageOfConstructionAvailability'),
      fetchInitialValueFromQueryString: true,
      controlConfig: {
        helpTooltipPosition: 'top',
      },
      dependsOn: {
        marketInsightsCity: {
          must: {
            haveValue: true,
          },
          soThatFilterCan: {
            fetchOptions: withSurveyedMarketOptions(marketInsightsAgeOfConstructionOptions),
          },
        },
      },
    },

    marketInsightsHistoricalAgeOfConstruction: {
      label: vm.$ts('marketInsights.filters.labels.ageOfConstruction'),
      type: 'select',
      helpText: vm.$ts('marketInsights.filters.helpText.ageOfConstructionAvailability'),
      fetchInitialValueFromQueryString: true,
      controlConfig: {
        helpTooltipPosition: 'top',
      },
      dependsOn: {
        marketInsightsCityRadio: {
          must: {
            haveValue: true,
          },
          soThatFilterCan: {
            fetchOptions: withSurveyedMarketOptions(marketInsightsAgeOfConstructionOptions),
          },
        },
      },
    },

    marketInsightsNumberOfStories: {
      label: vm.$ts('marketInsights.filters.labels.numberOfStories'),
      type: 'checkboxes',
      helpText: vm.$ts('marketInsights.filters.helpText.numberOfStoriesAvailability'),
      fetchInitialValueFromQueryString: true,
      dependsOn: {
        marketInsightsCity: {
          must: {
            haveValue: true,
          },
          soThatFilterCan: {
            fetchOptions: withSurveyedMarketOptions(marketInsightsNumberOfStoriesOptions),
          },
        },
      },
    },

    marketInsightsHistoricalNumberOfStories: {
      label: vm.$ts('marketInsights.filters.labels.numberOfStories'),
      type: 'checkboxes',
      helpText: vm.$ts('marketInsights.filters.helpText.numberOfStoriesAvailability'),
      fetchInitialValueFromQueryString: true,
      dependsOn: {
        marketInsightsCityRadio: {
          must: {
            haveValue: true,
          },
          soThatFilterCan: {
            fetchOptions: withSurveyedMarketOptions(marketInsightsNumberOfStoriesOptions),
          },
        },
      },
    },

    marketInsightsParkingAvailable: {
      label: vm.$ts('marketInsights.filters.labels.parkingAvailable'),
      type: 'select',
      helpText: vm.$ts('marketInsights.filters.helpText.parkingAvailability'),
      fetchInitialValueFromQueryString: true,
      controlConfig: {
        helpTooltipPosition: 'top',
      },
      fetcher: async () => marketInsightsParkingAvailableOptions,
    },

    marketInsightsParkingIncluded: {
      label: vm.$ts('marketInsights.filters.labels.parkingIncluded'),
      type: 'select',
      helpText: vm.$ts('marketInsights.filters.helpText.parkingAvailability'),
      fetchInitialValueFromQueryString: true,
      controlConfig: {
        helpTooltipPosition: 'top',
      },
      fetcher: async () => marketInsightsParkingIncludedOptions,
    },

    marketInsightsParkingType: {
      label: vm.$ts('marketInsights.filters.labels.parkingType'),
      type: 'checkboxes',
      fetchInitialValueFromQueryString: true,
      fetcher: async () => marketInsightsParkingTypeOptions,
    },

    marketInsightsPets: {
      label: vm.$ts('marketInsights.filters.labels.pets'),
      type: 'checkboxes',
      fetchInitialValueFromQueryString: true,
      fetcher: async () => prSelectOptions.petFriendlyTypes,
    },

    marketInsightsFSAWithCondition: {
      ...marketInsightsFSABase,
      ...marketInsightsDefaultFSAValues.length && {
        value: marketInsightsDefaultFSAValues,
      },
      dependsOn: {
        marketInsightsCity: {
          must: {
            haveValue: true,
          },
          soThatFilterCan: marketInsightsFSABase.dependsOn?.base?.soThatFilterCan ?? {},
        },
      },
      controlConfig: {
        ...isMarketComparisonNeighbourhoodRoute && {
          max: 4,
        },
        mapView: { cityIdKey: 'marketInsightsCity' },
      },
    },

    marketInsightsPortfolioCompareCity: {
      ...marketInsightsCityRadioBase,
      display: {
        headingGroup: vm.$ts('marketInsights.filters.headingGroups.portfolioAndMarket'),
      },
    },

    marketInsightsPortfolioCompareBeds: {
      ...marketInsightsBedroomsBase,
      display: {
        headingGroup: vm.$ts('marketInsights.filters.headingGroups.portfolioAndMarket'),
      },
    },

    marketInsightsPortfolioBuildingType: {
      ...marketInsightsBuildingTypeBase,
      display: {
        headingGroup: vm.$ts('marketInsights.filters.headingGroups.portfolioOnly'),
      },
    },

    marketInsightsPortfolioFSA: {
      ...marketInsightsFSABase,
      dependsOn: {
        marketInsightsPortfolioCompareCity: {
          must: {
            haveValue: true,
          },
          soThatFilterCan: marketInsightsFSABase.dependsOn?.base?.soThatFilterCan ?? {},
        },
      },
      controlConfig: {
        mapView: { cityIdKey: 'marketInsightsPortfolioCompareCity' },
      },
      display: {
        headingGroup: vm.$ts('marketInsights.filters.headingGroups.portfolioOnly'),
      },
    },

    marketInsightsPortfolioBuildings: {
      ...buildingsBase,
      fetcher: checkForRPMFetcher(vm, 'rpmProperties', async () => {
        try {
          const response = await properties.getPropertyOptions();
          return (response.data ?? [])
            .filter((b) => vm.$rsGlobals.$userHasAccess('buildings', 'read', b.id ?? 0))
            .map((b) => ({
              text: b.buildingName,
              value: `${b.id}`,
            }));
        } catch (err) {
          console.error(err);
          return [];
        }
      }),
      display: {
        headingGroup: vm.$ts('marketInsights.filters.headingGroups.portfolioOnly'),
      },
    },

    marketInsightsCompareBuildingType: {
      ...marketInsightsBuildingTypeBase,
      display: {
        headingGroup: vm.$ts('marketInsights.filters.headingGroups.marketOnly'),
      },
    },

    marketInsightsCompareAgeOfConstruction: {
      label: vm.$ts('marketInsights.filters.labels.ageOfConstruction'),
      type: 'select',
      helpText: vm.$ts('marketInsights.filters.helpText.ageOfConstructionAvailability'),
      fetchInitialValueFromQueryString: true,
      controlConfig: {
        helpTooltipPosition: 'top',
      },
      dependsOn: {
        marketInsightsPortfolioCompareCity: {
          must: {
            haveValue: true,
          },
          soThatFilterCan: {
            fetchOptions: withSurveyedMarketOptions(marketInsightsAgeOfConstructionOptions),
          },
        },
      },
      display: {
        headingGroup: vm.$ts('marketInsights.filters.headingGroups.marketOnly'),
      },
    },

    marketInsightsCompareNumberOfStories: {
      label: vm.$ts('marketInsights.filters.labels.numberOfStories'),
      type: 'checkboxes',
      helpText: vm.$ts('marketInsights.filters.helpText.numberOfStoriesAvailability'),
      fetchInitialValueFromQueryString: true,
      dependsOn: {
        marketInsightsPortfolioCompareCity: {
          must: {
            haveValue: true,
          },
          soThatFilterCan: {
            fetchOptions: withSurveyedMarketOptions(marketInsightsNumberOfStoriesOptions),
          },
        },
      },
      display: {
        headingGroup: vm.$ts('marketInsights.filters.headingGroups.marketOnly'),
      },
    },

    marketInsightsCompareFSA: {
      ...marketInsightsFSABase,
      dependsOn: {
        marketInsightsPortfolioCompareCity: {
          must: {
            haveValue: true,
          },
          soThatFilterCan: marketInsightsFSABase.dependsOn?.base?.soThatFilterCan ?? {},
        },
      },
      controlConfig: {
        mapView: { cityIdKey: 'marketInsightsPortfolioCompareCity' },
      },
      display: {
        headingGroup: vm.$ts('marketInsights.filters.headingGroups.marketOnly'),
      },
    },

    infinityPLPortfolioCompareCity: {
      label: vm.$ts('marketInsights.filters.labels.city'),
      type: 'radio',
      fetchInitialValueFromQueryString: true,
      clearButtonBehaviour: 'reset',
      required: true,
      fetcher: async () => getInfinityPLCityOptionsCached(),
      ...infinityPLCityOptions?.length && {
        value: `${infinityPLCityOptions[0]?.value}`,
      },
      display: {
        headingGroup: vm.$ts('marketInsights.filters.headingGroups.portfolioAndMarket'),
      },
    },

    infinityPLPortfolioBuildings: {
      label: vm.$ts('marketInsights.filters.labels.properties'),
      type: 'checkboxes',
      fetchInitialValueFromQueryString: true,
      dependsOn: {
        infinityPLPortfolioCompareCity: {
          must: {
            haveValue: true,
          },
          soThatFilterCan: {
            fetchOptions: async (dependentValue) => getInfinityPLPropertyOptionsByCity(dependentValue),
          },
        },
      },
      display: {
        headingGroup: vm.$ts('marketInsights.filters.headingGroups.portfolioOnly'),
      },
    },

    infinityPLPortfolioFSA: {
      ...marketInsightsFSABase,
      dependsOn: {
        infinityPLPortfolioCompareCity: {
          must: {
            haveValue: true,
          },
          soThatFilterCan: marketInsightsFSABase.dependsOn?.base?.soThatFilterCan ?? {},
        },
      },
      display: {
        headingGroup: vm.$ts('marketInsights.filters.headingGroups.portfolioOnly'),
      },
    },

    infinityPLCompareFSA: {
      ...marketInsightsFSABase,
      dependsOn: {
        infinityPLPortfolioCompareCity: {
          must: {
            haveValue: true,
          },
          soThatFilterCan: marketInsightsFSABase.dependsOn?.base?.soThatFilterCan ?? {},
        },
      },
      display: {
        headingGroup: vm.$ts('marketInsights.filters.headingGroups.marketOnly'),
      },
    },
  };
};
